/**
 * AiCloud Research Projects - Arastirma projeleri
 * Solo veya collaborative (tum platform agent'larinin gucu ile)
 * FLZ token odenir
 */

import axios from 'axios'
import { AlertCircle, FlaskConical, Plus, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../../config/api'

interface ResearchProject {
  id: number
  title: string
  description: string
  collaboration_mode: 'solo' | 'collaborative'
  status: string
  agent_count: number
  flz_spent: number
  progress_percent: number
  created_at: string
}

interface CreateForm {
  title: string
  description: string
  collaboration_mode: 'solo' | 'collaborative'
}

export default function ResearchProjectsPage() {
  const [projects, setProjects] = useState<ResearchProject[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<CreateForm>({ title: '', description: '', collaboration_mode: 'solo' })

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token')
      const resp = await axios.get(`${API_URL}/api/projects/my?type=research`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const data = resp.data as { projects?: ResearchProject[] } | ResearchProject[]
      setProjects(Array.isArray(data) ? data : (data.projects ?? []))
    } catch {
      setProjects([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void fetchProjects() }, [])

  const handleCreate = async () => {
    if (!form.title.trim()) {
      setError('Project title is required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/projects`, {
        ...form,
        project_type: 'research',
        is_private: true  // Research projeleri her zaman private
      }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setShowCreate(false)
      setForm({ title: '', description: '', collaboration_mode: 'solo' })
      void fetchProjects()
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } }
      setError(err.response?.data?.detail ?? 'Failed to create project')
    } finally {
      setSaving(false)
    }
  }

  const statusColor: Record<string, string> = {
    active: '#10b981',
    completed: '#3b82f6',
    paused: '#f59e0b',
    draft: '#6b7280'
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>Research Projects</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            Private research projects. Solo or collaborative with all platform agents.
          </p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
          <Plus size={14} /> New Research
        </button>
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {showCreate && (
        <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>New Research Project</h3>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Title</label>
            <input type="text" placeholder="Research title" value={form.title}
              onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Description</label>
            <textarea placeholder="What are you researching?" value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              rows={3}
              style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px', resize: 'vertical' }} />
          </div>

          {/* Collaboration modu */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Collaboration Mode</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              {[
                { value: 'solo' as const, icon: FlaskConical, label: 'Solo', desc: 'Just you and your agents', cost: 'Standard FLZ' },
                { value: 'collaborative' as const, icon: Users, label: 'Collaborative', desc: 'All platform agents contribute', cost: 'More FLZ, faster results' },
              ].map(opt => (
                <button key={opt.value}
                  onClick={() => setForm(p => ({ ...p, collaboration_mode: opt.value }))}
                  style={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px', background: form.collaboration_mode === opt.value ? 'rgba(139,92,246,0.1)' : 'var(--body-bg)', border: `1px solid ${form.collaboration_mode === opt.value ? '#8b5cf6' : 'var(--border-color)'}`, borderRadius: '8px', cursor: 'pointer', textAlign: 'left' }}>
                  <opt.icon size={16} color={form.collaboration_mode === opt.value ? '#8b5cf6' : 'var(--text-secondary)'} style={{ marginTop: '2px', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: form.collaboration_mode === opt.value ? '#8b5cf6' : 'var(--text-primary)' }}>{opt.label}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>{opt.desc}</div>
                    <div style={{ fontSize: '11px', color: '#f59e0b' }}>{opt.cost}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div style={{ padding: '10px 12px', background: 'rgba(139,92,246,0.05)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: '6px', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Research projects are always private. FLZ tokens are consumed based on agent usage and collaboration level.
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => { void handleCreate() }} disabled={saving}
              style={{ padding: '8px 16px', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '6px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Creating...' : 'Start Research'}
            </button>
            <button onClick={() => setShowCreate(false)}
              style={{ padding: '8px 16px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading...</div>
      ) : projects.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', background: 'var(--card-bg)', border: '1px dashed var(--border-color)', borderRadius: '10px' }}>
          <FlaskConical size={32} color="#8b5cf6" style={{ margin: '0 auto 12px' }} />
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>No research projects yet.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {projects.map(p => (
            <div key={p.id} style={{ padding: '16px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {p.collaboration_mode === 'collaborative'
                    ? <Users size={14} color="#8b5cf6" />
                    : <FlaskConical size={14} color="#8b5cf6" />
                  }
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>{p.title}</span>
                </div>
                <span style={{ fontSize: '11px', padding: '2px 8px', background: `${statusColor[p.status] ?? '#6b7280'}20`, color: statusColor[p.status] ?? '#6b7280', borderRadius: '4px', fontWeight: 600 }}>
                  {p.status}
                </span>
              </div>
              {p.description && (
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px' }}>{p.description}</p>
              )}
              {/* Progress bar */}
              <div style={{ marginBottom: '10px' }}>
                <div style={{ height: '4px', background: 'var(--body-bg)', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${p.progress_percent}%`, background: '#8b5cf6', borderRadius: '2px', transition: 'width 0.3s' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                <span>{p.collaboration_mode}</span>
                <span>{p.agent_count} agents</span>
                <span>{p.flz_spent} FLZ spent</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
